"use client"

/**
 * useViewConfig — read/write a module's list-view config in localStorage.
 *
 * Storage key: `tamda-view-config:<moduleKey>` (per-browser, per-user; no
 * backend sync). The stored value is merged against the current registry on
 * every read so the config stays valid when columns are added/removed in code:
 * unknown keys are dropped, new columns are appended per their `defaultVisible`.
 *
 * When nothing is stored the returned config equals `computeDefaultConfig`,
 * which reproduces the module's current behaviour (all default-visible columns,
 * original order, original pageSize).
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import type { ColumnMeta, ViewConfig, ViewConfigDefaults } from "./types"

const STORAGE_PREFIX = "tamda-view-config:"

export const viewConfigStorageKey = (moduleKey: string) => `${STORAGE_PREFIX}${moduleKey}`

/** Build the baseline config from a registry — the "localStorage empty" state. */
export function computeDefaultConfig(registry: ColumnMeta[], defaults: ViewConfigDefaults): ViewConfig {
  return {
    visibleKeys: registry.filter(c => c.defaultVisible || c.alwaysOn).map(c => c.key),
    order: registry.map(c => c.key),
    pageSize: defaults.pageSize,
    sortField: defaults.sortField,
    sortOrder: defaults.sortOrder,
    enableSearch: defaults.enableSearch ?? true,
    enableFilters: defaults.enableFilters ?? true,
    enableBulk: defaults.enableBulk ?? true,
  }
}

/**
 * Reconcile a stored config with the live registry. Safe when the registry
 * changed since the config was written.
 */
export function mergeConfig(
  stored: Partial<ViewConfig> | null | undefined,
  registry: ColumnMeta[],
  defaults: ViewConfigDefaults,
): ViewConfig {
  const base = computeDefaultConfig(registry, defaults)
  if (!stored) return base

  const registryKeys = new Set(registry.map(c => c.key))
  const alwaysOnKeys = registry.filter(c => c.alwaysOn).map(c => c.key)

  // Order: keep stored order (existing keys only), then append any new columns
  // at their registry position.
  const storedOrder = (stored.order ?? []).filter(k => registryKeys.has(k))
  const orderSet = new Set(storedOrder)
  const order = [...storedOrder]
  for (const key of base.order) {
    if (!orderSet.has(key)) order.push(key)
  }

  // Visible: intersect stored visible with the registry, add newly-introduced
  // default-visible columns, and force alwaysOn columns on.
  const storedVisible = stored.visibleKeys
    ? new Set(stored.visibleKeys.filter(k => registryKeys.has(k)))
    : new Set(base.visibleKeys)
  for (const key of base.order) {
    if (!(stored.order ?? []).includes(key)) {
      // Column added to the registry after this config was saved.
      const meta = registry.find(c => c.key === key)
      if (meta?.defaultVisible) storedVisible.add(key)
    }
  }
  for (const key of alwaysOnKeys) storedVisible.add(key)
  const visibleKeys = order.filter(k => storedVisible.has(k))

  // A sortField that no longer exists (or is not sortable) falls back to default.
  const sortField =
    stored.sortField && registry.some(c => c.key === stored.sortField && c.sortable)
      ? stored.sortField
      : base.sortField

  return {
    visibleKeys,
    order,
    pageSize: typeof stored.pageSize === "number" ? stored.pageSize : base.pageSize,
    sortField,
    sortOrder: sortField ? stored.sortOrder ?? base.sortOrder : base.sortOrder,
    enableSearch: typeof stored.enableSearch === "boolean" ? stored.enableSearch : base.enableSearch,
    enableFilters: typeof stored.enableFilters === "boolean" ? stored.enableFilters : base.enableFilters,
    enableBulk: typeof stored.enableBulk === "boolean" ? stored.enableBulk : base.enableBulk,
  }
}

/** Structural equality for two configs — used for dirty-state + default checks. */
export function viewConfigEqual(a: ViewConfig, b: ViewConfig): boolean {
  return (
    a.pageSize === b.pageSize &&
    (a.sortField ?? "") === (b.sortField ?? "") &&
    (a.sortOrder ?? "") === (b.sortOrder ?? "") &&
    a.enableSearch === b.enableSearch &&
    a.enableFilters === b.enableFilters &&
    a.enableBulk === b.enableBulk &&
    a.order.length === b.order.length &&
    a.order.every((k, i) => k === b.order[i]) &&
    a.visibleKeys.length === b.visibleKeys.length &&
    a.visibleKeys.every((k, i) => k === b.visibleKeys[i])
  )
}

function readStored(moduleKey: string): Partial<ViewConfig> | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(viewConfigStorageKey(moduleKey))
    return raw ? (JSON.parse(raw) as Partial<ViewConfig>) : null
  } catch {
    return null
  }
}

export interface UseViewConfigResult {
  config: ViewConfig
  setConfig: (next: ViewConfig) => void
  reset: () => void
  /** The "localStorage empty" config — target of the drawer's Reset button. */
  defaultConfig: ViewConfig
  /** True once the persisted value has been read on the client (post-hydration). */
  hydrated: boolean
}

export function useViewConfig(
  moduleKey: string,
  registry: ColumnMeta[],
  defaults: ViewConfigDefaults,
): UseViewConfigResult {
  // Start from defaults so SSR and the first client paint agree (no hydration
  // mismatch). The stored value is applied in an effect after mount.
  const [config, setConfigState] = useState<ViewConfig>(() => computeDefaultConfig(registry, defaults))
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setConfigState(mergeConfig(readStored(moduleKey), registry, defaults))
    setHydrated(true)
    // Registry/defaults are module-static; re-run only if the module changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey])

  const setConfig = useCallback(
    (next: ViewConfig) => {
      setConfigState(next)
      if (typeof window === "undefined") return
      try {
        window.localStorage.setItem(viewConfigStorageKey(moduleKey), JSON.stringify(next))
      } catch {
        /* localStorage unavailable (private mode / quota) — keep in-memory only. */
      }
    },
    [moduleKey],
  )

  const reset = useCallback(() => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(viewConfigStorageKey(moduleKey))
      } catch {
        /* ignore */
      }
    }
    setConfigState(computeDefaultConfig(registry, defaults))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey])

  const defaultConfig = useMemo(
    () => computeDefaultConfig(registry, defaults),
    // Registry/defaults are module-static.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moduleKey],
  )

  return { config, setConfig, reset, defaultConfig, hydrated }
}
